/**
 * Operator chat file uploads — London Smoke & Cure.
 * Path: operatorChatFiles/{chatId}/{messageId}/{safeFileName}
 * Max 120 MB per file (any format).
 */

import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from './firebase';

export const OPERATOR_CHAT_MAX_FILE_BYTES = 120 * 1024 * 1024;
export const OPERATOR_CHAT_FILES_PREFIX = 'operatorChatFiles';

/**
 * @param {File | Blob | null | undefined} file
 * @returns {string | null} error message or null if ok
 */
export function validateOperatorChatFile(file) {
    if (!file) return 'No file selected.';
    const size = Number(file.size);
    if (!Number.isFinite(size) || size <= 0) return 'File is empty.';
    if (size > OPERATOR_CHAT_MAX_FILE_BYTES) {
        return `File is too large (max ${formatOperatorChatFileBytes(OPERATOR_CHAT_MAX_FILE_BYTES)}).`;
    }
    return null;
}

/**
 * @param {number} bytes
 * @returns {string}
 */
export function formatOperatorChatFileBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Strip path separators and keep a usable file name.
 * @param {string} name
 * @returns {string}
 */
export function sanitizeOperatorChatFileName(name) {
    const base = String(name || 'file')
        .replace(/[/\\]/g, '_')
        .replace(/[^\w.\- ()[\]]+/g, '_')
        .trim();
    const clipped = base.slice(0, 180) || 'file';
    return clipped;
}

/**
 * @param {string} chatId
 * @param {string} messageId
 * @param {string} fileName
 * @returns {string}
 */
export function operatorChatFileStoragePath(chatId, messageId, fileName) {
    const safeChat = String(chatId || '').trim();
    const safeMsg = String(messageId || '').trim();
    const safeName = sanitizeOperatorChatFileName(fileName);
    if (!safeChat || !safeMsg) {
        throw new Error('chatId and messageId are required for file upload.');
    }
    return `${OPERATOR_CHAT_FILES_PREFIX}/${safeChat}/${safeMsg}/${safeName}`;
}

/**
 * Upload one file with progress callback. Returns attachment metadata.
 * @param {{
 *   file: File,
 *   chatId: string,
 *   messageId: string,
 *   onProgress?: (pct: number) => void,
 * }} args
 * @returns {Promise<{
 *   storagePath: string,
 *   fileName: string,
 *   contentType: string,
 *   sizeBytes: number,
 *   downloadUrl: string,
 * }>}
 */
export function uploadOperatorChatFile({ file, chatId, messageId, onProgress }) {
    const err = validateOperatorChatFile(file);
    if (err) return Promise.reject(new Error(err));

    const fileName = sanitizeOperatorChatFileName(file.name || 'file');
    const storagePath = operatorChatFileStoragePath(chatId, messageId, fileName);
    const contentType = file.type || 'application/octet-stream';
    const objectRef = ref(storage, storagePath);
    const task = uploadBytesResumable(objectRef, file, {
        contentType,
        customMetadata: {
            brand: 'London Smoke & Cure',
            chatId: String(chatId),
            messageId: String(messageId),
        },
    });

    return new Promise((resolve, reject) => {
        task.on(
            'state_changed',
            (snap) => {
                if (typeof onProgress === 'function' && snap.totalBytes > 0) {
                    onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
                }
            },
            (uploadErr) => reject(uploadErr),
            async () => {
                try {
                    const downloadUrl = await getDownloadURL(task.snapshot.ref);
                    resolve({
                        storagePath,
                        fileName,
                        contentType,
                        sizeBytes: Number(file.size) || 0,
                        downloadUrl,
                    });
                } catch (urlErr) {
                    reject(urlErr);
                }
            }
        );
    });
}

/**
 * Resolve a download URL for an existing attachment (if path present).
 * @param {string} storagePath
 * @returns {Promise<string>}
 */
export async function getOperatorChatFileUrl(storagePath) {
    if (!storagePath) throw new Error('storagePath is required.');
    return getDownloadURL(ref(storage, storagePath));
}
